import React, { useCallback, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Paper,
  Link,
  Grid,
} from "@mui/material";
import { MapRainforestMarker } from "types/map";
import RainforestTreeIcon from "components/icons/RainforestTreeIcon";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import ConstructionOutlinedIcon from "@mui/icons-material/ConstructionOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import { formatSqFtToKm2 } from "libraries/map";
import MapReferenceCompareTooltip from "./MapReferenceCompareTooltip";

import MapMarkerRainforestInfoDetails from "./MapMarkerRainforestInfoDetails";
import MapMarkerRainforestInfoSection from "./MapMarkerRainforestInfoSection";
import MapMarkerRainforestInfoProducts from "./MapMarkerRainforestInfoProducts";

type Props = {
  marker: MapRainforestMarker;
  squareFootage?: number;
};

const MapMarkerRainforestInfo = ({ marker, squareFootage }: Props) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const handleOpenDetails = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      marker.details && setIsDetailsOpen(true);
    },
    [marker]
  );

  const hasThreats = marker.threats && marker.threats.length > 0;
  const hasChallenges = marker.challenges && marker.challenges.length > 0;

  return (
    <Box
      sx={{
        p: 2,
        maxWidth: { sm: 500 },
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <RainforestTreeIcon sx={{ color: "success.main", fontSize: "1.2rem" }} />
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            color="text.primary"
          >
            {marker.name}
          </Typography>
        </Stack>
      </Stack>

      {marker.states && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mb: squareFootage ? 0.5 : 2 }}
        >
          <PlaceOutlinedIcon
            sx={{ color: "text.secondary", fontSize: "1rem" }}
          />
          <Typography variant="body2" color="text.secondary">
            {marker.states}
          </Typography>
        </Stack>
      )}

      {squareFootage && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <SquareFootIcon sx={{ color: "text.secondary", fontSize: "1rem" }} />
          <MapReferenceCompareTooltip squareFootage={squareFootage}>
            <Typography variant="body2" color="text.secondary">
              {formatSqFtToKm2(squareFootage)}
            </Typography>
          </MapReferenceCompareTooltip>
        </Stack>
      )}

      {marker.products && marker.products.length > 0 && (
        <MapMarkerRainforestInfoProducts products={marker.products} />
      )}

      <Grid container spacing={hasThreats && hasChallenges ? 2 : 0}>
        {hasThreats && (
          <Grid item xs={12} sm={hasChallenges ? 6 : 12}>
            <MapMarkerRainforestInfoSection
              title="Threats"
              icon={<WarningAmberIcon fontSize="small" />}
            >
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {marker.threats!.map((t, i) => (
                  <Typography
                    component="li"
                    key={i}
                    variant="body2"
                    color="text.primary"
                    sx={{ mb: 0.5 }}
                  >
                    {t}
                  </Typography>
                ))}
              </Box>
            </MapMarkerRainforestInfoSection>
          </Grid>
        )}

        {hasChallenges && (
          <Grid item xs={12} sm={hasThreats ? 6 : 12}>
            <MapMarkerRainforestInfoSection
              title="Challenges"
              icon={<ConstructionOutlinedIcon fontSize="small" />}
            >
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {marker.challenges!.map((c, i) => (
                  <Typography
                    component="li"
                    key={i}
                    variant="body2"
                    color="text.primary"
                    sx={{ mb: 0.5 }}
                  >
                    {c}
                  </Typography>
                ))}
              </Box>
            </MapMarkerRainforestInfoSection>
          </Grid>
        )}
      </Grid>

      {marker.partners && marker.partners.length > 0 && (
        <MapMarkerRainforestInfoSection
          title="Partners"
          icon={<GroupsOutlinedIcon fontSize="small" />}
        >
          <Stack spacing={1}>
            {marker.partners.map((p, i) => (
              <Stack
                key={i}
                direction="row"
                spacing={1}
                alignItems="center"
              >
                {p.logo && (
                  <Box
                    component="img"
                    src={p.logo}
                    alt={p.name}
                    sx={{ width: 24, height: 24, objectFit: "contain" }}
                  />
                )}
                <Box>
                  {p.url ? (
                    <Link
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      color="primary"
                      variant="body2"
                    >
                      {p.name}
                    </Link>
                  ) : (
                    <Typography variant="body2">{p.name}</Typography>
                  )}
                </Box>
              </Stack>
            ))}
          </Stack>
        </MapMarkerRainforestInfoSection>
      )}

      {marker.subgrantees && marker.subgrantees.length > 0 && (
        <MapMarkerRainforestInfoSection
          title="Subgrantees"
          icon={<GroupsOutlinedIcon fontSize="small" />}
        >
          <Stack spacing={1}>
            {marker.subgrantees.map((p, i) => (
              <Stack
                key={i}
                direction="row"
                spacing={1}
                alignItems="center"
              >
                {p.logo && (
                  <Box
                    component="img"
                    src={p.logo}
                    alt={p.name}
                    sx={{ width: 24, height: 24, objectFit: "contain" }}
                  />
                )}
                <Box>
                  {p.url ? (
                    <Link
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      color="primary"
                      variant="body2"
                    >
                      {p.name}
                    </Link>
                  ) : (
                    <Typography variant="body2">{p.name}</Typography>
                  )}
                </Box>
              </Stack>
            ))}
          </Stack>
        </MapMarkerRainforestInfoSection>
      )}

      {marker.contribution && (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            bgcolor: "success.50",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "success.100",
          }}
        >
          <Stack direction="row" spacing={1.5}>
            <HandshakeOutlinedIcon
              sx={{ color: "success.main", fontSize: "1.2rem", mt: 0.2 }}
            />
            <Box>
              <Typography
                variant="caption"
                color="success.dark"
                fontWeight="bold"
                display="block"
                sx={{ mb: 0.5 }}
              >
                FORECO CONTRIBUTION
              </Typography>
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ lineHeight: 1.5 }}
              >
                {marker.contribution}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      {marker.details && (
        <Box
          sx={{
            mt: 2,
            pt: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            fullWidth
            variant="contained"
            color="success"
            size="medium"
            startIcon={<TravelExploreIcon />}
            onClick={handleOpenDetails}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 0,
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
              },
            }}
          >
            Learn more
          </Button>
        </Box>
      )}

      {marker.details && (
        <MapMarkerRainforestInfoDetails
          open={isDetailsOpen}
          onClose={() => setIsDetailsOpen(false)}
          marker={marker}
        />
      )}
    </Box>
  );
};

export default MapMarkerRainforestInfo;
